// Web Push subscription helpers.
// Handles VAPID public key fetch, service worker registration, subscribe,
// unsubscribe, and status polling. Called from the "Enable alerts" buttons
// in Layout (staff) and ParticipantPortal.

import api from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export function isPushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function currentSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch { return null; }
}

export async function enableWebPush() {
  if (!isPushSupported()) throw new Error("Push notifications are not supported on this device.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission denied.");

  // Make sure /sw.js is registered — auth.jsx already does this, but be defensive.
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) reg = await navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" });
  await navigator.serviceWorker.ready;

  // If we're already subscribed with a valid key, reuse it. Otherwise drop & re-subscribe.
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    try { await existing.unsubscribe(); }
    catch (err) { console.debug("push unsubscribe (pre-resubscribe) failed:", err?.message); }
  }

  const { data } = await api.get("/push/vapid-public");
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.publicKey),
  });

  const raw = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: raw.endpoint, keys: raw.keys });
  return raw.endpoint;
}

export async function disableWebPush() {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try { await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } }); }
    catch (err) { console.debug("push unsubscribe (server) failed:", err?.message); }
    try { await sub.unsubscribe(); }
    catch (err) { console.debug("push unsubscribe (browser) failed:", err?.message); }
  }
}
